<template>
  <div class="col-12">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('teachers.edit_teacher_title') }}
            </h6>
          </div>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="t('teachers.loading_teacher')" />
      <div v-else-if="teacher" class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-12">
            <TeacherForm
              ref="teacherForm"
              :initial-data="teacher"
              :is-loading="isSubmitting"
              :is-edit-mode="true"
              :submit-text="t('teachers.save_changes')"
              @submit="updateTeacher"
              @cancel="navigateBack"
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />

            <div class="d-flex gap-3 mt-3 ms-3">
              <button
                type="button"
                class="btn btn-primary btn-sm flex-grow-1"
                :disabled="isSubmitting || !hasChanges"
                @click.prevent="submitForm"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin ms-2"></i>
                  {{ t('teachers.saving') }}
                </span>
                <span v-else>
                  <i class="fas fa-edit ms-2"></i>
                  {{ t('teachers.save_changes') }}
                </span>
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="navigateBack"
                :disabled="isSubmitting"
              >
                <i class="fas fa-times ms-2"></i>
                {{ t('teachers.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import TeacherForm from "@/components/features/teachers/TeacherForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const isLoading = ref(false);
const isSubmitting = ref(false);
const nationalities = ref([]);
const teacherForm = ref(null);
const originalTeacherData = ref(null);
const currentFormData = ref(null);

const hasChanges = computed(() => {
  if (!originalTeacherData.value || !currentFormData.value) return false;
  
  return Object.keys(currentFormData.value).some(key => {
    const currentValue = currentFormData.value[key];
    const originalValue = originalTeacherData.value[key];
    
    if (currentValue === null || originalValue === null) {
      return currentValue !== originalValue;
    }
    
    if (key.includes('_date')) {
      const currentDate = currentValue?.split('T')[0] || currentValue;
      const originalDate = originalValue?.split('T')[0] || originalValue;
      return currentDate !== originalDate;
    }
    
    if (key === 'salary') {
      const currentNum = typeof currentValue === 'string' 
        ? parseFloat(currentValue.replace(/,/g, '')) 
        : Number(currentValue);
      const originalNum = typeof originalValue === 'string' 
        ? parseFloat(String(originalValue).replace(/,/g, '')) 
        : Number(originalValue);
      return currentNum !== originalNum;
    }
    
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  });
});
const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
};

const fetchNationalities = async () => {
  try {
    const response = await api.get("/nationalities");
    nationalities.value = response.data.data;
  } catch (error) {
    console.error("Failed to load nationalities:", error);
  }
};

const teacher = computed(() => {
  const teacherData = store.state.teacher.currentTeacher;
  if (teacherData) {
    const rawActive = teacherData.is_active;
    const isActive = rawActive == null
      ? true
      : (rawActive !== false && rawActive !== 0 && rawActive !== "0");

    return {
      ...teacherData,
      password: "",
      password_confirmation: "",
      salary: teacherData.salary != null ? Number(teacherData.salary) : "",
      nationality_id: teacherData.nationality?.id || "",
      register_date:
        teacherData.register_date?.split("T")[0] ||
        new Date().toISOString().split("T")[0],
      hiring_date: teacherData.hiring_date?.split("T")[0] || "",
      image_url: teacherData.image_url,
      is_active: isActive,
    };
  }
  return null;
});

watch(teacher, (newTeacherData) => {
  if (newTeacherData && !originalTeacherData.value) {
    originalTeacherData.value = { ...newTeacherData };
    currentFormData.value = { ...newTeacherData };
  }
}, { immediate: true });

const submitForm = async () => {
  if (!teacherForm.value) return;

  isSubmitting.value = true;
  try {
    await teacherForm.value.submitForm();
  } catch (error) {
    console.error("Form submission error:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const fetchTeacher = async () => {
  isLoading.value = true;
  try {
    await fetchNationalities();
    await store.dispatch("teacher/fetchTeacher", route.params.id);
  } catch (error) {
    toast.error(t("teachers.loading_failed"));
    console.error("خطأ في الجلب:", error);
    router.push({ name: "teachers.index" });
  } finally {
    isLoading.value = false;
  }
};

const updateTeacher = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t("teachers.no_changes"));
    return;
  }

  isSubmitting.value = true;

  try {
    await store.dispatch("teacher/updateTeacher", {
      id: route.params.id,
      teacherData: formData,
    });

    toast.success(t("teachers.updated_successfully"));
    router.push({ name: "teachers.index" });
  } catch (error) {
    if (error.response?.status === 422 && error.response?.data?.errors) {
      teacherForm.value?.setBackendErrors(error.response.data.errors);

      const firstError = Object.values(error.response.data.errors)[0]?.[0];
      if (firstError) {
        toast.error(firstError);
      }
    } else {
      toast.error(error.response?.data?.message || t("teachers.update_failed"));
    }

    console.error("خطأ في التحديث:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleValidationError = () => {
  toast.warning(t("teachers.fill_required_fields"));
};

const navigateBack = () => {
  router.push({ name: "teachers.index" });
};

onMounted(() => fetchTeacher());
</script>